import { customLog } from 'helpers/customLog';

import clsx from 'clsx';
import React from 'react';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, MARKS } from '@contentful/rich-text-types';
import { Image } from '@unpic/react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/core/accordion';
import { useAnalytics } from '@/lib/gatrack';

type RichTextRendererProps = {
  document: Document;
  paragraphClassName?: string;
  headingClassName?: string;
  linkClassName?: string;
  className?: string;
  unOrderedClasName?: string;
  embededAssetClassName?: string;
  imgWidth?: number;
  imgHeight?: number;
  imgClassName?: string;
  isExpandable?: boolean;
  bulletDivClassName?: string;
  accordionContainerClassName?: string;
  accordionTitleClassName?: string;
  accordionParaClassName?: any;
  triggerIconClassName?: any;
  characterCount?: number;
  isTerms?: boolean;
  analyticsContext?: string;
};
function wrapParagraphsInDiv(nodes: React.ReactNode[]): React.ReactNode[] {
  const wrapped: React.ReactNode[] = [];
  let buffer: React.ReactNode[] = [];

  const isTextBlock = (node: React.ReactNode): boolean => {
    if (!React.isValidElement(node)) return false;
    const type = node.type;
    if (typeof type === 'string') {
      return ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(type);
    }

    return false;
  };
  nodes.forEach((node, index) => {
    if (isTextBlock(node)) {
      buffer.push(node);
    } else {
      if (buffer.length > 0) {
        wrapped.push(<div key={`group-${index}`}>{buffer}</div>);
        buffer = [];
      }
      wrapped.push(node);
    }
  });

  // Final flush
  if (buffer.length > 0) {
    wrapped.push(<div key={`group-final`}>{buffer}</div>);
  }

  return wrapped;
}

const getRenderOptions = (
  paragraphClassName?: string,
  headingClassName?: string,
  linkClassName?: string,
  unOrderedClasName?: string,
  imgWidth?: number,
  imgHeight?: number,
  imgClassName?: string,
  isExpandable?: boolean,
  bulletDivClassName?: string,
  accordionContainerClassName?: string,
  accordionTitleClassName?: string,
  accordionParaClassName?: any,
  triggerIconClassName?: any,
  characterCount?: number,
  isTerms?: boolean,
  trackContentExpanded?: any,
  trackCtaClicked?: any,
  analyticsContext?: string
) => ({
  renderNode: {
    [BLOCKS.HEADING_1]: (node: any, children: React.ReactNode) => {
      return <h2 className={clsx(paragraphClassName, headingClassName)}>{children}</h2>;
    },
    [BLOCKS.HEADING_2]: (node: any, children: React.ReactNode) => {
      return <p className={clsx(paragraphClassName, headingClassName)}>{children}</p>;
    },
    [BLOCKS.HEADING_3]: (node: any, children: React.ReactNode) => {
      return <p className={clsx(paragraphClassName, headingClassName)}>{children}</p>;
    },
    [BLOCKS.HEADING_4]: (node: any, children: React.ReactNode) => {
      return <p className={clsx(paragraphClassName, headingClassName)}>{children}</p>;
    },
    [BLOCKS.HEADING_5]: (node: any, children: React.ReactNode) => {
      return <p className={clsx(paragraphClassName, headingClassName)}>{children}</p>;
    },
    [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
      // Combine text content of this paragraph
      const textValue = node.content.map((child: { value?: string }) => child.value || '').join('');

      // Check if paragraph is empty (no children or text)
      const isEmpty = !children || React.Children.count(children) === 0 || !textValue;

      return (
        <p
          className={clsx('preserve-newline whitespace-pre-line', paragraphClassName)}
          style={isEmpty ? { minHeight: '1em' } : undefined} // keep space for empty paragraph
        >
          {children}
        </p>
      );
    },
    // [BLOCKS.PARAGRAPH]: (node: any, children: React.ReactNode) => {
    //   const textValue = node.content
    //     .map((child: { value: any; }) => child.value || '')
    //     .join('');
    //   if (!textValue) {
    //     // If paragraph is empty, render a break
    //     return <div style={{ height: '1em' }} />;
    //     // or use <br /> if you prefer a simple break:
    //     // return <br />;
    //   }

    //   return (
    //     <p className={clsx('preserve-newline', paragraphClassName)}>
    //       {children}
    //     </p>
    //   );
    //   // const textContent = Array.isArray(children)
    //   //   ? children.map(child => (typeof child === 'string' ? child : '')).join('')
    //   //   : typeof children === 'string'
    //   //     ? children
    //   //     : '';
    //   // const htmlContent = (
    //   //   isCharacterLimit ? truncate(textContent, characterCount || 50) : textContent
    //   // ).replace(/\n/g, '<br />');
    //   // if (htmlContent && !isTerms) {
    //   //   return (
    //   //     <p className={paragraphClassName} dangerouslySetInnerHTML={{ __html: htmlContent }} />
    //   //   );
    //   // } else {
    //   //   return (
    //   //     <p className={paragraphClassName}>
    //   //       {isCharacterLimit
    //   //         ? truncate(textContent, characterCount ? characterCount : 50)
    //   //         : children}
    //   //     </p>
    //   //   );
    //   // }
    // },
    [BLOCKS.LIST_ITEM]: (node: any, children: React.ReactNode) => {
      return <li className={clsx('text-base leading-relaxed', paragraphClassName)}>{children}</li>;
    },

    [BLOCKS.OL_LIST]: (node: any, children: React.ReactNode) => {
      return (
        <ol
          className={clsx(
            'marker:content-[counter(list-item) "."] list-decimal pl-10',
            paragraphClassName
          )}
        >
          {children}
        </ol>
      );
    },

    [BLOCKS.UL_LIST]: (node: any, children: React.ReactNode) => {
      return (
        <ul className={clsx('list-disc space-y-2 pl-5', paragraphClassName, unOrderedClasName)}>
          {children}
        </ul>
      );
    },
    [BLOCKS.EMBEDDED_ASSET]: (node: any, children: React.ReactNode) => {
      return (
        <div className={clsx(imgClassName)}>
          <Image
            width={imgWidth ? imgWidth : 0}
            height={imgHeight ? imgHeight : 0}
            layout="constrained"
            className="bg-cover bg-no-repeat"
            alt="thumbnail"
            // background="auto"
            src={`https://${node.data.target.fields.file.url}`}
          />
        </div>
      );
    },
    [INLINES.HYPERLINK]: (node: any, children: React.ReactNode) => {
      const renderTextWithMarks = (content: any[]) =>
        content.map((item, i) => {
          let text = item.value;

          if (item.marks?.length) {
            item.marks.forEach((mark: any) => {
              switch (mark.type) {
                case MARKS.BOLD:
                  text = <strong key={i}>{text}</strong>;
                  break;
                case MARKS.ITALIC:
                  text = <em key={i}>{text}</em>;
                  break;
                case MARKS.UNDERLINE:
                  text = (
                    <span key={i} style={{ textDecoration: 'underline' }}>
                      {text}
                    </span>
                  );
                  break;
              }
            });
          }

          return <React.Fragment key={i}>{text}</React.Fragment>;
        });

      const rawUri = node.data.uri || '';
      const [cleanUrl, targetFlag] = rawUri.split('|');

      const isCallLink = cleanUrl.startsWith('tel:');
      const isMailLink = cleanUrl.startsWith('mailto:');

      const openInNewTab = targetFlag === 'blank';

      const isExternal = !isCallLink && !isMailLink;
      customLog('Rendering hyperlink:', {
        cleanUrl,
        openInNewTab,
        isExternal,
      });
      return (
        <a
          href={cleanUrl}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          onClick={() => {
            trackCtaClicked({
              component: 'Hyperlink component',
              context: 'hyperlink tag',
              name: renderTextWithMarks(node.content),
              outbound: openInNewTab || isExternal,
              link_text: renderTextWithMarks(node.content),
              link_url: cleanUrl,
            });
          }}
          className={clsx(paragraphClassName, linkClassName)}
        >
          {renderTextWithMarks(node.content)}
        </a>
      );
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node: any, children: React.ReactNode) => {
      // target the contentType of the EMBEDDED_ENTRY to display as you need
      return isExpandable ? (
        <div className={clsx(`pb-2`, accordionContainerClassName)}>
          <Accordion type="single" collapsible className="flex min-w-full flex-col gap-3">
            <AccordionItem
              key={`accordion-${node?.data?.target?.fields?.title}}`}
              value={`accordion-${node?.data?.target?.fields?.title}`}
              className="flex flex-col gap-2 rounded-[8px] border-0 bg-color-background-bottomsheet-t-c p-4"
              onClick={event => {
                // Check if accordion is currently closed (about to expand)
                const accordionItem = event.currentTarget.closest('[data-state]');
                const currentState = accordionItem?.getAttribute('data-state');
                if (currentState === 'closed') {
                  trackContentExpanded({
                    name: node?.data?.target?.fields?.title || 'Accordion Content',
                    context: analyticsContext || 'rich-text-content-expansion',
                    component: 'accordion',
                  });
                }
              }}
            >
              {node?.data?.target?.fields?.title ? (
                <AccordionTrigger
                  className="align-center flex items-center justify-between gap-8 !py-0 text-left outline-0 md:!p-[4px] lg:!p-[10px]"
                  isTerms={isTerms}
                  triggerIconClassName={triggerIconClassName}
                >
                  <span className={clsx('font-[700]', accordionTitleClassName)}>
                    {node?.data?.target?.fields?.title}
                  </span>
                </AccordionTrigger>
              ) : null}
              {node?.data?.target?.fields?.terms ? (
                <AccordionContent
                  className={clsx('flex justify-center text-color-content-text-secondary')}
                >
                  <div className={accordionParaClassName.width}>
                    <RichTextRenderer
                      document={node?.data?.target?.fields?.terms}
                      imgHeight={20}
                      imgWidth={20}
                      className="flex cursor-pointer items-center"
                      unOrderedClasName={unOrderedClasName}
                      paragraphClassName={clsx(
                        'text-color-content-text-secondary pb-2',
                        accordionParaClassName.class
                      )}
                      linkClassName={clsx(
                        'underline font-[700] text-color-content-text-primary',
                        linkClassName
                      )}
                      isTerms={isTerms}
                      analyticsContext={analyticsContext}
                    />
                  </div>
                </AccordionContent>
              ) : null}
            </AccordionItem>
          </Accordion>
        </div>
      ) : (
        <div className="flex flex-col">
          {node?.data?.target?.fields?.title ? (
            <h2 className="items-start p-0 text-left font-[700] text-color-content-text-primary">
              {node?.data?.target?.fields?.title}
            </h2>
          ) : null}
          {node?.data?.target?.fields?.terms ? (
            <RichTextRenderer
              document={node?.data?.target?.fields?.terms}
              imgHeight={20}
              imgWidth={20}
              className="flex cursor-pointer items-center"
              unOrderedClasName={unOrderedClasName}
              paragraphClassName={clsx(
                'text-color-content-text-secondary pb-2',
                accordionParaClassName.class
              )}
              linkClassName={clsx(
                'underline font-[700] text-color-content-text-primary',
                linkClassName
              )}
              isTerms={isTerms}
              analyticsContext={analyticsContext}
            />
          ) : null}
        </div>
      );
    },
  },
});

export const RichTextRenderer: React.FC<RichTextRendererProps> = ({
  document,
  paragraphClassName,
  headingClassName,
  linkClassName,
  unOrderedClasName,
  className,
  embededAssetClassName,
  imgWidth = 40,
  imgHeight = 40,
  imgClassName,
  isExpandable,
  bulletDivClassName,
  accordionContainerClassName,
  accordionTitleClassName,
  accordionParaClassName = {},
  triggerIconClassName,
  characterCount = 50,
  isTerms = false,
  analyticsContext,
}) => {
  const { trackContentExpanded, trackCtaClicked } = useAnalytics();

  const options = getRenderOptions(
    paragraphClassName,
    headingClassName,
    linkClassName,
    unOrderedClasName,
    imgWidth,
    imgHeight,
    imgClassName,
    isExpandable,
    bulletDivClassName,
    accordionContainerClassName,
    accordionTitleClassName,
    accordionParaClassName,
    triggerIconClassName,
    characterCount,
    isTerms,
    trackContentExpanded,
    trackCtaClicked,
    analyticsContext
  );

  // Step 1: Render raw nodes
  const rawOutput = documentToReactComponents(document, options);

  // Step 2: Wrap paragraphs
  const wrappedOutput = Array.isArray(rawOutput) ? wrapParagraphsInDiv(rawOutput) : rawOutput;

  return <div className={className}>{wrappedOutput}</div>;
};
